import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ItemSearchResponseModel } from '../models/item-search-response-model';
import { ItemResponseModelV2, SupplierV2 } from '../models/item-response-model-v2';
import { ItemEditRequestModel } from '../models/item-edit-request-model';
import { ItemsService } from '../items.service';
import { ItemStateValidationService } from '../item-state-validation.service';

@Component({
    selector: 'edit-item-suppliers',
    standalone: true,
    imports: [CommonModule, FormsModule],
    providers: [ItemsService, ItemStateValidationService],
    templateUrl: './edit-item-suppliers.component.html'
})
export class EditItemSuppliersComponent implements OnInit {
    @Input() itemModel: ItemSearchResponseModel;
    @ViewChild('supplierForm') supplierForm: NgForm;
    
    public suppliers: SupplierV2[] = [];
    public selectedMainSupplierId: string = '';
    public selectedCoreItemOwnerId: string = '';
    public selectedItemState: string = '';
    public originalItemState: string = '';
    private loadedItem: ItemResponseModelV2 | null = null;
    public itemStateOptions = [
        { value: '10', text: 'NEW' },
        { value: '20', text: 'ACTIVE' },
        { value: '30', text: 'INACTIVE' },
        { value: '99', text: 'DELETED' }
    ];
    public loading: boolean = true;

    public get isActiveStateValid(): boolean {
        if (this.selectedItemState !== '20') return true;
        return this.loadedItem != null &&
            this.itemStateValidationService.canItemStateBeSetToActive(this.loadedItem);
    }

    public get availableItemStateOptions(): { value: string; text: string }[] {
        if (this.originalItemState === '99') {
            const deletedOption = { value: '99', text: 'DELETED' };
            if (!this.suppliers?.length) {
                return [{ value: '10', text: 'NEW' }, deletedOption];
            }
            const canBeActive = this.loadedItem != null &&
                this.itemStateValidationService.canItemStateBeSetToActive(this.loadedItem);
            return canBeActive
                ? [{ value: '20', text: 'ACTIVE' }, deletedOption]
                : [{ value: '30', text: 'INACTIVE' }, deletedOption];
        }
        const currentOption = this.itemStateOptions.find(o => o.value === this.originalItemState);
        const deletedOption = { value: '99', text: 'DELETED' };
        return currentOption && currentOption.value !== '99'
            ? [currentOption, deletedOption]
            : [deletedOption];
    }

    constructor(
        public activeModal: NgbActiveModal,
        private itemsService: ItemsService,
        private itemStateValidationService: ItemStateValidationService
    ) {}

    ngOnInit(): void {
        // Fetch item details to get all suppliers
        this.itemsService.getItem(this.itemModel.itemId).subscribe({
            next: (result) => {
                this.loadedItem = result;
                this.suppliers = result.suppliers ?? [];
                
                // Set current item state
                this.originalItemState = this.itemModel.itemState ?? '';
                this.selectedItemState = this.itemModel.itemState ?? '';

                // Find and set current main supplier
                const mainSupplier = this.suppliers.find(s => s.supplierDetails.isMainSupplier);
                if (mainSupplier) {
                    this.selectedMainSupplierId = mainSupplier.supplierDetails.supplierId;
                }
                
                // Find and set current core item owner
                const coreItemOwner = this.suppliers.find(s => s.supplierDetails.isCoreItemOwner);
                if (coreItemOwner) {
                    this.selectedCoreItemOwnerId = coreItemOwner.supplierDetails.supplierId;
                }
                
                this.loading = false;
            },
            error: (error) => {
                console.error('Error loading suppliers:', error);
                this.loading = false;
            }
        });
    }

    public save() {
        this.loading = true;
        
        // Create the request model
        const requestModel: ItemEditRequestModel = {
            itemId: this.itemModel.itemId,
            mainSupplier: this.selectedMainSupplierId,
            coreItemOwner: this.selectedCoreItemOwnerId || '',
            itemState: this.selectedItemState
        };
        
        console.log('Saving supplier configuration:', requestModel);
        
        // Call the API to update the item
        this.itemsService.updateItem(requestModel).subscribe({
            next: (result) => {
                console.log('Item updated successfully:', result);
                this.loading = false;
                this.activeModal.close({ action: 'save', data: result });
            },
            error: (error) => {
                console.error('Error updating item:', error);
                this.loading = false;
                alert('Failed to update the item. Please try again.');
            }
        });
    }
    
    public cancel() {
        this.activeModal.dismiss('cancel');
    }

    public getSupplierDisplayName(supplier: SupplierV2): string {
        return `${supplier.supplierDetails.supplierId}`;
    }
}
